import * as React from "react";
import { useDroppable, useDndMonitor } from "@dnd-kit/core";
import { calcDropIdx, UNIT_SIZE, GAP_SIZE } from "./TlCommon";

let getGradeColor = (grade: string) => {
  switch (grade) {
    case "S":
      return "text-legendary-2";
    case "A":
      return "text-epic-2";
    case "B":
      return "text-rare-2";
    case "C":
      return "text-uncommon-2";
    case "D":
    case "F":
      return "text-common";
    default:
      return "";
  }
};

const TierContainer = React.memo(
  ({
    label,
    children,
    itemCount,
    unitSize: baseUnitSize,
  }: {
    label: string;
    children: (
      dropIdx: number,
      dragStarted: boolean,
    ) => JSX.Element | Array<JSX.Element>;
    itemCount: number;
    unitSize?: number;
  }) => {
    const unitSize = baseUnitSize || UNIT_SIZE;
    const rowLength = Math.floor(800 / (unitSize + GAP_SIZE));
    const overRef = React.useRef(false);
    const { isOver, setNodeRef } = useDroppable({
      id: label,
    });
    overRef.current = isOver;
    const [dragStarted, setDragStarted] = React.useState(false);
    const [dropIdx, setDropIdx] = React.useState(99);

    const onDragMove = React.useCallback((ev: any) => {
      if (overRef.current) {
        setDropIdx(
          calcDropIdx(
            ev.active.rect.current.translated,
            ev.over?.rect,
            unitSize,
          ),
        );
      } else {
        setDropIdx(99);
      }
    }, []);

    const onDragStart = React.useCallback((ev: any) => {
      setDragStarted(true);
      onDragMove(ev);
    }, []);

    const onDragEnd = React.useCallback((ev: any) => {
      setDragStarted(false);
      setDropIdx(99);
    }, []);

    useDndMonitor({
      onDragStart: onDragStart,
      onDragMove: onDragMove,
      onDragEnd: onDragEnd,
    });

    const style = React.useMemo(
      () => ({
        boxShadow: isOver ? `inset 0 0 10px var(--primary-11)` : "none",
      }),
      [isOver],
    );

    const rowCount = Math.max(
      Math.ceil((itemCount + (dropIdx !== 99 ? 1 : 0)) / rowLength),
      1,
    );

    return (
      <div
        className="my-1 flex items-center w-full bg-bg2 rounded"
        style={{
          height: 12 + Math.max(rowCount, 1) * 86 + "px",
        }}
        ref={setNodeRef}
      >
        <div
          className={`select-none rounded-l h-full px-2 flex items-center justify-center text-black text-[36px] font-montserrat font-semibold w-[56px] text-center border-y-[2px] border-x-[2px] border-white2 ${getGradeColor(
            label,
          )}`}
        >
          {label}
        </div>
        <div
          className={`pt-[2px] flex px-3 gap-3 w-full h-full border-t border-r border-b rounded-r border-white2 relative`}
          style={style}
        >
          {children(dropIdx, dragStarted)}
        </div>
      </div>
    );
  },
);

export default TierContainer;
